import type { StaffRole, UserDetail } from '@planuze/admin-contracts';
import { Button, StatusBadge } from '@planuze/ui';
import { ShieldBan, ShieldCheck, UserCog } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../content/sections/users.js';
import { fmtDateTime } from '../../lib/format.js';
import { type SummaryMetric, SummaryMetrics } from '../detail-summary.js';
import { ChangeUserAccessModal } from './modals/change-user-access-modal.js';
import { ImpersonateModal } from './modals/impersonate-modal.js';

interface Props {
  readonly user: UserDetail;
  readonly staffRole: StaffRole;
}

/* Rótulos amigáveis dos providers OAuth conhecidos (ADR-0145); provider desconhecido cai no
   próprio identificador capitalizado. GitHub/Google são nomes próprios (exceção §4.11). */
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  github: 'GitHub',
  google: 'Google',
};

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/* Header do detalhe: identidade + ação Impersonar (apenas super_admin, §4.18) + faixa de
   métricas-resumo derivada dos arrays já carregados (ADR-0707). A vigência das licenses segue a
   mesma regra da aba (sem expiresAt = perpétua; futura = ativa; passada = expirada) e a "última
   atividade" é o max de lastUsedAt das sessões e lastSeenAt dos devices. */
export function UserDetailHeader({ user, staffRole }: Props) {
  const copy = useUsersContent();
  const canManageAccess = staffRole === 'super_admin';
  const canImpersonate = canManageAccess && user.status === 'active';
  const emailVerified = user.emailVerifiedAt !== null;
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  const metrics = useMemo<readonly SummaryMetric[]>(() => {
    const now = Date.now();
    let ativas = 0;
    let expiradas = 0;
    let perpetuas = 0;
    for (const license of user.licenses) {
      if (license.expiresAt === null) perpetuas += 1;
      else if (new Date(license.expiresAt).getTime() > now) ativas += 1;
      else expiradas += 1;
    }

    const sessoesAtivas = user.sessions.filter((s) => s.revokedAt === null).length;

    let lastActivity: string | null = null;
    const activityTimestamps = [
      ...(user.licensingAvailability.sessions === 'available'
        ? user.sessions.map((s) => s.lastUsedAt)
        : []),
      ...(user.licensingAvailability.devices === 'available'
        ? user.devices.map((d) => d.lastSeenAt)
        : []),
    ];
    for (const ts of activityTimestamps) {
      if (lastActivity === null || new Date(ts).getTime() > new Date(lastActivity).getTime()) {
        lastActivity = ts;
      }
    }

    const items: SummaryMetric[] = [
      { label: copy.detailHeader.summaryOrgs, value: user.orgs.length },
      {
        label: copy.detailHeader.summaryDevices,
        value: user.licensingAvailability.devices === 'available' ? user.devices.length : '—',
        ...(user.licensingAvailability.devices === 'unavailable' && {
          hint: copy.detailHeader.unavailableMetric,
        }),
      },
      {
        label: copy.detailHeader.summaryLicenses,
        value: user.licensingAvailability.licenses === 'available' ? user.licenses.length : '—',
        hint:
          user.licensingAvailability.licenses === 'available'
            ? copy.detailHeader.licensesHint(ativas, expiradas, perpetuas)
            : copy.detailHeader.unavailableMetric,
      },
      {
        label: copy.detailHeader.summaryActiveSessions,
        value:
          user.licensingAvailability.sessions === 'available'
            ? copy.detailHeader.sessionsFraction(sessoesAtivas, user.sessions.length)
            : '—',
        ...(user.licensingAvailability.sessions === 'unavailable' && {
          hint: copy.detailHeader.unavailableMetric,
        }),
      },
    ];
    if (lastActivity !== null) {
      items.push({
        label: copy.detailHeader.summaryLastActivity,
        value: fmtDateTime(lastActivity),
      });
    }
    return items;
  }, [user, copy]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {user.avatarUrl !== null && (
            <img
              src={user.avatarUrl}
              alt={copy.detailHeader.avatarAlt(user.email)}
              className="size-12 shrink-0 rounded-full border border-border object-cover"
            />
          )}
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-foreground">{user.email}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">@{user.username}</span>
              {user.name !== null && (
                <span className="text-sm text-muted-foreground">{user.name}</span>
              )}
              <StatusBadge variant={emailVerified ? 'success' : 'neutral'} dot>
                {emailVerified
                  ? copy.detailHeader.emailVerified
                  : copy.detailHeader.emailUnverified}
              </StatusBadge>
              <StatusBadge variant={user.status === 'blocked' ? 'destructive' : 'success'} dot>
                {user.status === 'blocked'
                  ? copy.detailHeader.accountBlocked
                  : copy.detailHeader.accountActive}
              </StatusBadge>
              {user.oauthProviders.map((provider) => (
                <StatusBadge key={provider} variant="info">
                  {providerLabel(provider)}
                </StatusBadge>
              ))}
            </div>
            {user.status === 'blocked' && (
              <div className="space-y-0.5 text-muted-foreground text-xs">
                {user.blockedAt !== null && (
                  <p>{copy.detailHeader.blockedAt(fmtDateTime(user.blockedAt))}</p>
                )}
                {user.blockedReason !== null && (
                  <p>{copy.detailHeader.blockedReason(user.blockedReason)}</p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canImpersonate && (
            <Button variant="outline" onClick={() => setImpersonateOpen(true)}>
              <UserCog className="mr-2 size-4" aria-hidden="true" />
              {copy.detailHeader.impersonate}
            </Button>
          )}
          {canManageAccess && (
            <Button
              variant={user.status === 'active' ? 'destructive' : 'outline'}
              onClick={() => setAccessOpen(true)}
            >
              {user.status === 'active' ? (
                <ShieldBan className="mr-2 size-4" aria-hidden="true" />
              ) : (
                <ShieldCheck className="mr-2 size-4" aria-hidden="true" />
              )}
              {user.status === 'active' ? copy.detailHeader.block : copy.detailHeader.unblock}
            </Button>
          )}
        </div>
        <ImpersonateModal
          open={impersonateOpen}
          onClose={() => setImpersonateOpen(false)}
          userId={user.id}
          userEmail={user.email}
        />
        <ChangeUserAccessModal
          open={accessOpen}
          onClose={() => setAccessOpen(false)}
          userId={user.id}
          userEmail={user.email}
          currentStatus={user.status}
        />
      </div>
      <SummaryMetrics items={metrics} />
    </div>
  );
}
